import { KeyboardArrowDown, KeyboardArrowRight } from '@styled-icons/material';
import { BorderBox, Box, Stack } from '@trmediaab/zebra';
import PropTypes from 'prop-types';
import { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';

import RaceInfo from '~/components/RaceInfo';
import RoundLegNavigator from '~/components/RoundNavigation/RoundLegNavigator';
import Spinner from '~/components/Spinner';
import EquipmentInfo from '~/components/StartList/EquipmentInfo';
import Time from '~/components/Time';
import WysiwygText from '~/components/WysiwygText';
import { access_control } from '~/containers/Auth/AccessControl';
import Interview from '~/containers/Interview';
import {
  closeInterviewArchiveStart,
  openInterviewArchiveStart,
} from '~/containers/Interview/actions';
import RoundLeg from '~/containers/RoundLeg';
import { colors, spacing } from '~/utils/css-mixins';
import media from '~/utils/media';

import Archive from './Archive';
import PreliminaryEquipment from './PreliminaryEquipment';
import { getInterviewText } from './utils';

const StyledWysiwygText = styled(WysiwygText)`
  font-size: 14px;
  line-height: 22px;
`;

const ArrowDown = styled(KeyboardArrowDown)`
  fill: ${colors.link};
`;

const ArrowRight = styled(KeyboardArrowRight)`
  fill: ${colors.link};
`;

const TextHead = styled.div`
  margin-bottom: 10px;
  font-size: 16px;
  line-height: 23px;

  ${media.phone`
    font-size: 18px;
    line-height: 25px;
  `}
`;

const ArchiveLink = styled.a`
  margin-left: ${spacing.siteGutter}px;
  font-weight: 600;

  &:hover {
    cursor: pointer;
  }

  svg {
    position: relative;
    top: -1px;
  }
`;

const StyledTime = styled(Time)`
  color: ${colors.textmeta};
`;

const TextFooter = styled.div`
  margin-top: ${spacing.siteGutter * 0.5}px;
  max-width: ${spacing.siteContentWidth};
  display: flex;
  font-size: 12px;
  line-height: 20px;
`;

const mapStateToProps = state => ({
  archive: state.interviewArchive,
  ...state.location.payload,
});

const mapDispatchToProps = function (dispatch) {
  return {
    archiveToggleClick: function (open, interview, page) {
      if (open) {
        dispatch(
          openInterviewArchiveStart(
            interview.start.id,
            interview.start.horse.id,
            interview.race.start_time,
            page,
          ),
        );
      } else {
        dispatch(closeInterviewArchiveStart());
      }
    },
    archiveMoreClick: function (interview, page) {
      dispatch(
        openInterviewArchiveStart(
          interview.start.id,
          interview.start.horse.id,
          interview.race.start_time,
          page + 1,
        ),
      );
    },
    archiveCloseClick: function () {
      dispatch(closeInterviewArchiveStart());
    },
  };
};

class Item extends Component {
  render() {
    const {
      interview,
      archive,
      archiveToggleClick,
      archiveMoreClick,
      archiveCloseClick,
      live,
    } = this.props;

    return (
      <Box
        bg="white"
        px={live ? undefined : '3,5'}
        py={live ? undefined : '3,5'}
        key={interview.id}
      >
        <TextHead>
          <b>
            {interview.start.start_number}.&nbsp;
            {interview.start.horse.name}{' '}
          </b>
          {live && interview.leg && <span>{interview.leg} </span>}
          {`– ${interview.start.driver}`}{' '}
          {interview.start.trainer && <span>({interview.start.trainer})</span>}
        </TextHead>

        <PreliminaryEquipment interview={interview} />

        <StyledWysiwygText
          html={getInterviewText(interview.text, interview.byline)}
        />

        {!live && (
          <TextFooter justify="space-between">
            <StyledTime
              date={new Date(interview.created)}
              format="dd MMMM 'kl.' HH:mm"
            />

            {interview.has_archive && (
              <ArchiveLink
                color="#3385ff"
                className={
                  interview.start.id === archive.start_id
                    ? 'opened'
                    : 'collapsed'
                }
                onClick={() =>
                  archiveToggleClick(
                    interview.start.id !== archive.start_id,
                    interview,
                    1,
                  )
                }
              >
                Intervjuarkiv
                {interview.start.id === archive.start_id ? (
                  <ArrowDown size={20} />
                ) : (
                  <ArrowRight size={20} />
                )}
              </ArchiveLink>
            )}
          </TextFooter>
        )}

        {interview.start.id === archive.start_id && (
          <Fragment>
            {((archive.page === 1 && !archive.fetching) ||
              archive.page > 1) && (
              <Archive
                total={archive.count}
                interviews={archive.results}
                closeClick={archiveCloseClick}
                moreClick={() => archiveMoreClick(interview, archive.page)}
              />
            )}
            {(archive.fetching || archive.error) && (
              <Spinner error={archive.error} loading={archive.fetching} />
            )}
          </Fragment>
        )}
      </Box>
    );
  }
}

export const InterviewItem = connect(mapStateToProps, mapDispatchToProps)(Item);

export default access_control(
  class Interviews extends Component {
    static propTypes = {
      round: PropTypes.object.isRequired,
      slug: PropTypes.string.isRequired,
      leg_number: PropTypes.number.isRequired,
      location: PropTypes.object,
    };

    static defaultProps = {
      leg_number: 1,
    };

    render() {
      const { slug, leg_number, round } = this.props;

      return (
        <Box>
          <RoundLegNavigator round={round} />
          <RoundLeg
            slug={slug}
            leg={leg_number}
            render={({ race }) => (
              <Fragment>
                <RaceInfo race={race} primaryBetType={round.bet_type} />
                <BorderBox
                  sx={{
                    borderTop: '2px solid',
                    borderColor: 'background.secondary',
                  }}
                >
                  <Interview
                    slug={slug}
                    leg={leg_number}
                    render={interviews => (
                      <Stack between="2">
                        {interviews.map(interview => (
                          <InterviewItem
                            interview={interview}
                            key={interview.id}
                          />
                        ))}
                        {interviews.length > 0 && (
                          <Box px="3,5">
                            <EquipmentInfo />
                          </Box>
                        )}
                      </Stack>
                    )}
                  />
                </BorderBox>
              </Fragment>
            )}
          />
        </Box>
      );
    }
  },
  'premium',
  'Intervjuer',
);
